// src/pages/QuestionnairePage/steps/Step3ADisease.tsx

import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Activity, Sparkles, AlertTriangle, Calendar } from 'lucide-react';

const StepCard = styled(motion.div)`
  background: var(--card-bg);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border-medium);
  border-radius: 24px;
  padding: 32px 20px;
  box-shadow:
    0 8px 32px rgba(123, 104, 238, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7B68EE, #87CEEB);
  }

  @media (max-width: 375px) {
    padding: 28px 16px;
  }

  [data-theme="dark"] & {
    box-shadow:
      0 8px 32px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::before {
      background: linear-gradient(90deg, #9d8eee, #a0d8f0);
    }
  }
`;

const Question = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 24px 0;
  line-height: 1.3;
  display: flex;
  align-items: flex-start;
  gap: 10px;

  svg {
    color: #7B68EE;
    flex-shrink: 0;
    margin-top: 2px;
  }

  [data-theme="dark"] & {
    svg {
      color: #C4B5FD;
    }
  }
`;

const YesNoGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const ChipButton = styled(motion.button)<{ $active: boolean }>`
  flex: 1;
  padding: 20px 24px;
  border-radius: 16px;
  border: 2px solid ${props => props.$active 
    ? '#7B68EE' 
    : 'rgba(123, 104, 238, 0.2)'
  };
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #7B68EE, #9966CC)' 
    : 'rgba(255, 255, 255, 0.8)'
  };
  color: ${props => props.$active 
    ? '#FFFFFF !important' 
    : '#000000 !important'
  };
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.$active 
    ? '0 4px 16px rgba(123, 104, 238, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.05)'
  };
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  svg {
    color: ${props => props.$active ? '#FFFFFF !important' : '#7B68EE !important'};
  }
  
  &:hover {
    transform: translateY(-2px);
    border-color: #7B68EE;
    box-shadow: 0 6px 20px rgba(123, 104, 238, 0.25);
  }

  &:active {
    transform: translateY(0);
  }

  [data-theme="dark"] & {
    border-color: ${props => props.$active
      ? '#C4B5FD'
      : 'rgba(157, 142, 238, 0.3)'
    };
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #9d8eee, #b896dc)'
      : 'rgba(255, 255, 255, 0.08)'
    };
    color: ${props => props.$active
      ? '#FFFFFF !important'
      : '#FFFFFF !important'
    };
    box-shadow: ${props => props.$active
      ? '0 4px 16px rgba(157, 142, 238, 0.4)'
      : '0 2px 8px rgba(0, 0, 0, 0.3)'
    };

    svg {
      color: ${props => props.$active ? '#FFFFFF !important' : '#C4B5FD !important'};
    }

    &:hover {
      border-color: #C4B5FD;
      box-shadow: 0 6px 20px rgba(157, 142, 238, 0.35);
    }
  }
`;

const ButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonLabel = styled.span`
  font-size: 15px;
`;

const Alert = styled(motion.div)`
  margin-top: 20px;
  padding: 20px;
  border-radius: 16px;
  background: linear-gradient(135deg,
    rgba(239, 68, 68, 0.1),
    rgba(220, 38, 38, 0.05)
  );
  border: 2px solid rgba(239, 68, 68, 0.2);
  text-align: center; // ✅ FIX: центрирование всего контента

  [data-theme="dark"] & {
    background: linear-gradient(135deg,
      rgba(239, 68, 68, 0.15),
      rgba(220, 38, 38, 0.1)
    );
    border: 2px solid rgba(239, 68, 68, 0.3);
  }
`;

const AlertTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #EF4444;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  justify-content: center; // ✅ FIX: центрирование иконки + текста
  gap: 8px;

  svg {
    color: #EF4444;
  }
`;

const AlertText = styled.p`
  font-size: 14px;
  color: rgba(0,0,0,0.7);
  margin: 0 0 16px 0;
  line-height: 1.5;
  text-align: center; // ✅ FIX: центрирование текста

  [data-theme="dark"] & {
    color: rgba(255, 255, 255, 0.85);
  }
`;

const ConsultButton = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 20px;
  background: linear-gradient(135deg, #7B68EE, #9966CC);
  color: white !important;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(123, 104, 238, 0.3);

  svg {
    color: white !important;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(123, 104, 238, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

interface Step3ADiseaseProps {
  value: boolean | null;
  onChange: (hasDisease: boolean) => void;
}

export const Step3ADisease: React.FC<Step3ADiseaseProps> = ({ value, onChange }) => {
  return (
    <StepCard
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Question>
        <Activity size={24} />
        <span>Есть ли у Вас розацеа, атопический дерматит или иные кожные заболевания?</span>
      </Question>
      
      <YesNoGroup>
        <ChipButton
          $active={value === true}
          onClick={() => onChange(true)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonIcon>
            <Activity size={28} />
          </ButtonIcon>
          <ButtonLabel>Да, есть</ButtonLabel>
        </ChipButton>
        
        <ChipButton
          $active={value === false}
          onClick={() => onChange(false)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonIcon>
            <Sparkles size={28} />
          </ButtonIcon>
          <ButtonLabel>Нет</ButtonLabel>
        </ChipButton>
      </YesNoGroup>

      {value === true && (
        <Alert
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <AlertTitle>
            <AlertTriangle size={20} />
            <span>Важно!</span>
          </AlertTitle>
          <AlertText>
            Введение ретинола при кожных заболеваниях возможно только после консультации специалиста.
          </AlertText>
          <AlertText>
            📌 Вы можете записаться на <strong>бесплатную консультацию</strong> к косметологу в наши Центры Диагностики Кожи.
          </AlertText>
          <ConsultButton 
            href="https://geltek.ru/skin-diagnostic-center/" 
            target="_blank" 
            rel="noopener noreferrer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Calendar size={18} />
            <span>Записаться на консультацию</span>
          </ConsultButton>
        </Alert>
      )}
    </StepCard>
  );
};